import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import { Card, Button, Row, Col, Spin, Badge } from "antd";
import SwitchIcon from "~/components/icons/SwitchIcon";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { showAppModal, getJobsSummary } from "~/redux/actions/app";
import { talentInfo } from "~/redux/actions/talent";
import settings from "~/settings";

const SiderJobsSummary = (props) => {
  // 默认为招聘者视角 (true: 招聘者, false: 应聘者)
  const [isRecruiterView, setIsRecruiterView] = useState(props.isRecruiterView);

  const hasUnreadApplicationMsg = props.messageCount.jobApplicationMsgCount > 0;
  const hasPendingApplications =
    props.jobsSummary.pending_applications_count > 0;

  // 从localStorage读取用户选择的视角
  useEffect(() => {
    // props 中的 isRecruiterView 优先级最高
    if (props.isRecruiterView !== undefined && props.isRecruiterView !== null) {
      return;
    }
    // 其次判断是否有未读的申请消息，如果有有则默认显示应聘者视角
    if (hasUnreadApplicationMsg) {
      setIsRecruiterView(false);
      return;
    }
    // 其次判断是否有待处理申请，如果有有则默认显示招聘者视角
    if (hasPendingApplications) {
      setIsRecruiterView(true);
      return;
    }
    // 最后判断localStorage中是否保存了用户选择的视角
    if (typeof localStorage !== "undefined") {
      const savedView = localStorage.getItem("jobs-summary-view");
      if (savedView !== null) {
        setIsRecruiterView(savedView === "recruiter");
      }
    }
  }, [hasUnreadApplicationMsg, hasPendingApplications]);

  useEffect(() => {
    if (props.token && !props.talentInfoResult.progress) {
      props.talentInfo(props.token);
    }
    // 获取jobs summary数据
    if (props.token) {
      props.getJobsSummary(props.token);
    }
  }, [props.token]);

  // 切换视角并保存到localStorage
  const handleViewSwitch = (checked) => {
    const newView = checked;
    setIsRecruiterView(newView);
    if (props.token) {
      props.getJobsSummary(props.token);
    }

    if (typeof localStorage !== "undefined") {
      localStorage.setItem(
        "jobs-summary-view",
        newView ? "recruiter" : "candidate"
      );
    }
  };

  const handleOngoingRecruitments = () => {
    if (!props.token) {
      props.showAppModal("PhoneSigninModal");
      return;
    }
    window.open(`/recruitments?status=published`, "_blank");
  };

  const handlePendingApplications = () => {
    if (!props.token) {
      props.showAppModal("PhoneSigninModal");
      return;
    }
    window.open(`/recruitments/applications`, "_blank");
  };

  const handleMyApplications = () => {
    if (!props.token) {
      props.showAppModal("PhoneSigninModal");
      return;
    }
    window.open(`/applications/jobs_channel`, "_blank");
  };

  const handleViewResume = () => {
    if (!props.token) {
      props.showAppModal("PhoneSigninModal");
      return;
    }
    window.open(`${settings.talentUrl}/me/profile`, "_blank");
  };

  // 招聘者视角内容
  const renderRecruiterView = () => (
    <Row
      gutter={[8, 8]}
      className="jobs-summary-content"
    >
      <Col span={12}>
        <div
          className="summary-item"
          onClick={handleOngoingRecruitments}
        >
          <span className="summary-value">
            {props.jobsSummary.ongoing_jobs_count || 0}
          </span>
          <span className="summary-label">进行中的招聘</span>
        </div>
      </Col>
      <Col span={12}>
        <div
          className="summary-item"
          onClick={handlePendingApplications}
        >
          <Badge
            className="summary-value-badge"
            dot={props.jobsSummary.pending_applications_count || 0 > 0}
          ></Badge>
          <span className="summary-value">
            {props.jobsSummary.pending_applications_count || 0}
          </span>
          <span className="summary-label">待处理的申请</span>
        </div>
      </Col>
    </Row>
  );

  // 应聘者视角内容
  const renderCandidateView = () => (
    <Row
      gutter={[8, 8]}
      className="jobs-summary-content"
    >
      <Col span={12}>
        <div
          className="summary-item"
          onClick={handleMyApplications}
        >
          <Badge
            className="summary-value-badge"
            dot={props.messageCount.jobApplicationMsgCount > 0}
          ></Badge>
          <span className="summary-value">
            {props.jobsSummary.my_applications_count || 0}
          </span>
          <span className="summary-label">我的申请</span>
        </div>
      </Col>
      <Col span={12}>
        <div
          className="summary-item"
          onClick={handleViewResume}
        >
          <span className="summary-value resume-view-percentage">
            {props.talentInfoResult.progress?.proportion || 0}%
          </span>
          <span className="summary-label">查看简历</span>
        </div>
      </Col>
    </Row>
  );

  return (
    <Card className="sider-jobs-summary-card">
      <div className="sider-jobs-summary">
        <div className="sider-jobs-summary-header">
          <div className="sider-jobs-summary-title">
            {isRecruiterView ? "招聘管理" : "我的申请"}
          </div>
          <div className="view-switch">
            <Button
              type="link"
              onClick={() => handleViewSwitch(!isRecruiterView)}
              size="small"
              style={{
                padding: "0",
              }}
            >
              <div className="switch-icon">
                <SwitchIcon />
                {isRecruiterView ? "切换至应聘者" : "切换至招聘者"}
              </div>
            </Button>
          </div>
        </div>
        <div className="sider-jobs-summary-separator" />
        {isRecruiterView ? renderRecruiterView() : renderCandidateView()}
      </div>
    </Card>
  );
};

const mapStateToProps = (state) => ({
  token: state.user.token,
  user: state.user.user,
  talentInfoResult: state.talent.talentInfoResult,
  jobsSummary: state.app.jobsSummary,
  isLoadingJobsSummary: state.app.request.GET_JOBS_SUMMARY_REQUEST,
  messageCount: state.message.messageCount,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      showAppModal,
      talentInfo,
      getJobsSummary,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(SiderJobsSummary);
